'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, NavArrowLeft, NavArrowRight } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { use } from 'react';
import { Button as RAC_Button } from 'react-aria-components';
import { Link } from '~/components/Link';
import styles from './CheckoutProgressStepper.module.css';
import type { CheckoutProgressStepperProviderProps } from './CheckoutProgressStepper.types';
import { CheckoutProgressStepperContext } from './CheckoutProgressStepperContext';
const stepStatusFor = (index: number, currentStepIndex: number) => {
  if (index < currentStepIndex) return 'completed';
  if (index === currentStepIndex) return 'current';
  return 'pending';
};
const VISIBLE_STEPS_XS = 3;
const VISIBLE_STEPS_SM = 4;
function Provider(t0) {
  const $ = _c(12);
  const {
    children,
    currentStepIndex,
    steps,
    onBack,
    hrefForStep
  } = t0;
  const clampedStepIndex = steps.length > 0 ? Math.min(Math.max(0, currentStepIndex), steps.length - 1) : 0;
  let t1;
  if ($[0] !== clampedStepIndex || $[1] !== steps) {
    t1 = {
      currentStepIndex: clampedStepIndex,
      steps
    };
    $[0] = clampedStepIndex;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hrefForStep || $[4] !== onBack) {
    t2 = {
      onBack,
      hrefForStep
    };
    $[3] = hrefForStep;
    $[4] = onBack;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      state: t1,
      actions: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const value = t3;
  let t4;
  if ($[9] !== children || $[10] !== value) {
    t4 = <CheckoutProgressStepperContext.Provider value={value}>{children}</CheckoutProgressStepperContext.Provider>;
    $[9] = children;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
const Frame = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <nav className={styles.frame} aria-label="Checkout progress">{children}</nav>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const TopRow = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className={styles.topRow}>{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const BackButton = () => {
  const $ = _c(3);
  const ctx = use(CheckoutProgressStepperContext);
  if (!ctx) {
    return null;
  }
  const {
    actions
  } = ctx;
  if (!actions.onBack) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NavArrowLeft decorative={true} size="large" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== actions.onBack) {
    t1 = <RAC_Button type="button" className={styles.backButton} onPress={actions.onBack} aria-label="Go back to previous step" data-test-id="checkout-progress-stepper-back">{t0}</RAC_Button>;
    $[1] = actions.onBack;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const StepLabel = t0 => {
  const $ = _c(17);
  const {
    label,
    status,
    index,
    hrefForStep
  } = t0;
  const t = useTranslations("journey");
  let t1;
  if ($[0] !== status || $[1] !== t) {
    t1 = status === "completed" && <span className="sr-only"> {t("stepper_step_completed")}</span>;
    $[0] = status;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const completedSuffix = t1;
  if (status === "completed" && hrefForStep) {
    let t2;
    if ($[3] !== hrefForStep || $[4] !== index) {
      t2 = hrefForStep(index);
      $[3] = hrefForStep;
      $[4] = index;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const t3 = `checkout-progress-stepper-step-link-${index}`;
    let t4;
    if ($[6] !== completedSuffix || $[7] !== label) {
      t4 = <span className={styles.stepLink}>{label}{completedSuffix}</span>;
      $[6] = completedSuffix;
      $[7] = label;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
      t5 = <Link href={t2} isUnstyled={true} dataTestId={t3}>{t4}</Link>;
      $[9] = t2;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  const t2 = styles[`label_${status}`];
  let t3;
  if ($[13] !== completedSuffix || $[14] !== label || $[15] !== t2) {
    t3 = <span className={t2}>{label}{completedSuffix}</span>;
    $[13] = completedSuffix;
    $[14] = label;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
function buildBreadcrumb(steps: string[], currentStepIndex: number, visibleCount: number) {
  const total = steps.length;
  const startIndex = Math.max(0, Math.min(currentStepIndex, total - visibleCount));
  const visibleSteps = steps.slice(startIndex, startIndex + visibleCount);
  const showEllipsisAfter = startIndex + visibleCount < total;
  return {
    startIndex,
    visibleSteps,
    showEllipsisAfter
  };
}
const BreadcrumbList = t0 => {
  const $ = _c(24);
  const {
    className,
    label,
    testId,
    steps,
    currentStepIndex,
    visibleCount,
    hrefForStep
  } = t0;
  let showEllipsisAfter;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== currentStepIndex || $[2] !== hrefForStep || $[3] !== label || $[4] !== steps || $[5] !== testId || $[6] !== visibleCount) {
    const {
      startIndex,
      visibleSteps,
      showEllipsisAfter: t5
    } = buildBreadcrumb(steps, currentStepIndex, visibleCount);
    showEllipsisAfter = t5;
    t1 = className;
    t2 = label;
    t3 = testId;
    let t6;
    if ($[12] !== currentStepIndex || $[13] !== hrefForStep || $[14] !== startIndex) {
      t6 = (stepLabel, i) => {
        const index = startIndex + i;
        const status = stepStatusFor(index, currentStepIndex);
        return <li key={stepLabel} className={styles.breadcrumbGroup} aria-current={index === currentStepIndex ? "step" : undefined}>{i > 0 && <span className={styles.breadcrumbSeparator} aria-hidden={true}><NavArrowRight decorative={true} size="small" /></span>}<StepLabel label={stepLabel} status={status} index={index} hrefForStep={hrefForStep} /></li>;
      };
      $[12] = currentStepIndex;
      $[13] = hrefForStep;
      $[14] = startIndex;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t4 = visibleSteps.map(t6);
    $[0] = className;
    $[1] = currentStepIndex;
    $[2] = hrefForStep;
    $[3] = label;
    $[4] = steps;
    $[5] = testId;
    $[6] = visibleCount;
    $[7] = showEllipsisAfter;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    showEllipsisAfter = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[16] !== showEllipsisAfter) {
    t5 = showEllipsisAfter && <li className={styles.breadcrumbGroup}><span className={styles.breadcrumbSeparator} aria-hidden={true}><NavArrowRight decorative={true} size="small" /></span><span className={styles.breadcrumbEllipsis} aria-hidden={true}>…</span></li>;
    $[16] = showEllipsisAfter;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <ol className={t1} aria-label={t2} data-test-id={t3}>{t4}{t5}</ol>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};
const Steps = () => {
  const $ = _c(18);
  const ctx = use(CheckoutProgressStepperContext);
  if (!ctx) {
    return null;
  }
  const {
    state: t0,
    actions: t1
  } = ctx;
  const {
    steps,
    currentStepIndex
  } = t0;
  const {
    hrefForStep
  } = t1;
  let t2;
  if ($[0] !== currentStepIndex || $[1] !== hrefForStep || $[2] !== steps) {
    let t3;
    if ($[4] !== currentStepIndex || $[5] !== hrefForStep) {
      t3 = (label, index) => {
        const status = stepStatusFor(index, currentStepIndex);
        return <li key={label} className={styles.stepItem} aria-current={index === currentStepIndex ? "step" : undefined}>{status !== "pending" && <span className={styles.stepMarker} data-status={status} aria-hidden={true}>{status === "completed" && <Check decorative={true} size="small" />}</span>}<StepLabel label={label} status={status} index={index} hrefForStep={hrefForStep} /></li>;
      };
      $[4] = currentStepIndex;
      $[5] = hrefForStep;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = steps.map(t3);
    $[0] = currentStepIndex;
    $[1] = hrefForStep;
    $[2] = steps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <ol className={styles.stepList} data-test-id="checkout-progress-stepper-steps">{t2}</ol>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  if ($[9] !== currentStepIndex || $[10] !== hrefForStep || $[11] !== steps) {
    t4 = <BreadcrumbList className={styles.stepListBreadcrumbXs} label="Checkout progress" testId="checkout-progress-stepper-steps-breadcrumb-xs" steps={steps} currentStepIndex={currentStepIndex} visibleCount={VISIBLE_STEPS_XS} hrefForStep={hrefForStep} />;
    t5 = <BreadcrumbList className={styles.stepListBreadcrumbSm} label="Checkout progress" testId="checkout-progress-stepper-steps-breadcrumb-sm" steps={steps} currentStepIndex={currentStepIndex} visibleCount={VISIBLE_STEPS_SM} hrefForStep={hrefForStep} />;
    $[9] = currentStepIndex;
    $[10] = hrefForStep;
    $[11] = steps;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const ProgressBar = () => {
  const $ = _c(7);
  const ctx = use(CheckoutProgressStepperContext);
  if (!ctx) {
    return null;
  }
  const {
    state: t0
  } = ctx;
  const {
    steps,
    currentStepIndex
  } = t0;
  if (steps.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== currentStepIndex || $[1] !== steps) {
    let t2;
    if ($[3] !== currentStepIndex) {
      t2 = (label, index) => {
        const segmentStatus = stepStatusFor(index, currentStepIndex);
        return <div key={label} className={styles.progressSegment} data-status={segmentStatus}><div className={styles.progressSegmentFill} data-status={segmentStatus} /></div>;
      };
      $[3] = currentStepIndex;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = steps.map(t2);
    $[0] = currentStepIndex;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className={styles.progressBarSegments} aria-hidden={true} data-test-id="checkout-progress-stepper-bar-segments">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const CheckoutProgressStepper = {
  Provider,
  Frame,
  TopRow,
  BackButton,
  Steps,
  ProgressBar
};
export { CheckoutProgressStepper };